import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Typography,
  useTheme,
  useMediaQuery,
  InputAdornment,
  IconButton,
  Grid,
  Tooltip,
} from "@mui/material";
import "moment/locale/es";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";

import NotesTable from "../panel/panelNotes/NotesTable";
import { useFetchNotas, fetchNotasDesdeAPI } from "../../../hooks/useGetNotes";
import { Loading } from "../../sharedComponents/Loading";
import { DataNotFound } from "../../sharedComponents/DataNotFound";
import { normalizeText } from "../../user/panel/panelNotes/HighlightText";

export const NotesPanel = ({
  token,
  idDocument,
  selectedDate,
  selectedIdSection,
  refresh,
  setRefresh,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSources, setSelectedSources] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [openMedia, setOpenMedia] = useState(false);
  const [openTypeMedia, setOpenTypeMedia] = useState(false);
  const [selectedMediaTypes, setSelectedMediaTypes] = useState([]);

  const { notas, loadingNote } = useFetchNotas(
    token,
    selectedDate,
    selectedIdSection,
    refresh,
    idDocument,
  );

  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(notas);
  }, [notas]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const nuevasNotas = await fetchNotasDesdeAPI(
          token,
          selectedDate,
          selectedIdSection,
          idDocument
        );

        setItems((prevItems) => {
          if (JSON.stringify(prevItems) !== JSON.stringify(nuevasNotas)) {
            return nuevasNotas;
          }
          return prevItems;
        });
      } catch (error) {
        console.error("Error al actualizar las notas:", error);
      }
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [token, selectedDate, selectedIdSection, idDocument]);

  const availableSources = useMemo(() => {
    const sourceSet = new Set();
    items.forEach((nota) => {
      nota.testigo?.forEach((testigoItem) => {
        const medio = testigoItem?.medio;
        if (medio && medio.trim()) {
          sourceSet.add(medio.trim());
        }
      });
    });
    return Array.from(sourceSet).sort();
  }, [items]);

  const availableMediaTypes = useMemo(() => {
    const typeSet = new Set();
    items.forEach((nota) => {
      nota.testigo?.forEach((testigoItem) => {
        const medio = testigoItem?.type_medio;
        if (medio && medio.trim()) {
          typeSet.add(medio.trim());
        }
      });
    });
    return Array.from(typeSet).sort();
  }, [items]);

  const handleSourceChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedSources(typeof value === "string" ? value.split(",") : value);
  };

  const handleRemoveSource = (sourceToRemove) => {
    setSelectedSources(
      selectedSources.filter((source) => source !== sourceToRemove)
    );
  };

  const handleClearFilters = () => {
    setSelectedSources([]);
    setSelectedMediaTypes([]);
    setSearchTerm("");
  };

  const handleMediaTypeChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedMediaTypes(typeof value === "string" ? value.split(",") : value);
  };

  const handleRemoveMediaType = (typeToRemove) => {
    setSelectedMediaTypes(selectedMediaTypes.filter((t) => t !== typeToRemove));
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const filteredData = items.filter((item) => {
    const searchTermNormalized = normalizeText(searchTerm.toLowerCase());
    const tituloNormalized = normalizeText(item.titulo.toLowerCase());
    const sintesisNormalized = normalizeText(
      (item.sintesis || "").toLowerCase()
    );

    const matchesSearchTerm =
      tituloNormalized.includes(searchTermNormalized) ||
      sintesisNormalized.includes(searchTermNormalized);

    const mediosNota =
      item.testigo?.map((t) => t?.medio?.trim()).filter(Boolean) || [];
    const matchesSource =
      selectedSources.length === 0 ||
      mediosNota.some((medio) => selectedSources.includes(medio));

    const tiposMedioNota =
      item.testigo?.map((t) => t?.type_medio?.trim()).filter(Boolean) || [];
    const matchesMediaType =
      selectedMediaTypes.length === 0 ||
      tiposMedioNota.some((tipo) => selectedMediaTypes.includes(tipo));

    return matchesSearchTerm && matchesSource && matchesMediaType;
  });

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: 2,
        borderRadius: "16px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={isMobile ? 10 : 11}>
            <TextField
              placeholder="Buscar notas por título o síntesis"
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      aria-label="Limpiar búsqueda"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
              }}
            />
          </Grid>
          <Grid item xs={isMobile ? 2 : 1}>
            <Tooltip title="Filtros">
              <IconButton
                onClick={toggleFilters}
                color={
                  showFilters || selectedSources.length > 0
                    ? "primary"
                    : "default"
                }
                sx={{
                  bgcolor: showFilters
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                  borderRadius: "12px",
                  padding: isMobile ? "8px" : "12px",
                  border: showFilters
                    ? "1px solid rgba(25, 118, 210, 0.5)"
                    : "none",
                }}
              >
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {showFilters && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "12px",
            bgcolor: "rgba(0,0,0,0.02)",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              Filtrar por:
            </Typography>
            <IconButton size="small" onClick={toggleFilters}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <FormControl fullWidth size="small">
            <InputLabel id="source-select-label">Medios</InputLabel>
            <Select
              labelId="source-select-label"
              id="source-select"
              multiple
              open={openMedia}
              onOpen={() => setOpenMedia(true)}
              onClose={() => setOpenMedia(false)}
              value={selectedSources}
              onChange={(event) => {
                handleSourceChange(event);
                setOpenMedia(false);
              }}
              input={<OutlinedInput label="Medios" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.length > 0 ? (
                    selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Todos los medios
                    </Typography>
                  )}
                </Box>
              )}
              sx={{
                borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0,0,0,0.15)",
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    width: 250,
                  },
                },
              }}
            >
              {availableSources.map((source) => (
                <MenuItem key={source} value={source}>
                  {source}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel id="media-type-select-label">Tipo de Medio</InputLabel>
            <Select
              labelId="media-type-select-label"
              id="media-type-select"
              multiple
              open={openTypeMedia}
              onOpen={() => setOpenTypeMedia(true)}
              onClose={() => setOpenTypeMedia(false)}
              value={selectedMediaTypes}
              onChange={(event) => {
                handleMediaTypeChange(event);
                setOpenTypeMedia(false);
              }}
              input={<OutlinedInput label="Tipo de Medio" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.length > 0 ? (
                    selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Todos los tipos
                    </Typography>
                  )}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                    width: 250,
                  },
                },
              }}
            >
              {availableMediaTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(selectedSources.length > 0 || searchTerm) && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Chip
                label="Limpiar filtros"
                onClick={handleClearFilters}
                deleteIcon={<ClearIcon />}
                onDelete={handleClearFilters}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
          )}
        </Box>
      )}

      {!showFilters &&
        (selectedSources.length > 0 || selectedMediaTypes.length > 0) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mr: 1, display: "flex", alignItems: "center" }}
            >
              <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
              Filtros:
            </Typography>

            {selectedSources.map((source) => (
              <Chip
                key={`source-${source}`}
                label={`Medio: ${source}`}
                size="small"
                onDelete={() => handleRemoveSource(source)}
                color="primary"
                variant="outlined"
              />
            ))}

            {selectedMediaTypes.map((type) => (
              <Chip
                key={`type-${type}`}
                label={`Tipo: ${type}`}
                size="small"
                onDelete={() => handleRemoveMediaType(type)}
                color="primary"
                variant="outlined"
              />
            ))}

            {(selectedSources.length > 0 || selectedMediaTypes.length > 0) && (
              <Chip
                label="Limpiar"
                size="small"
                onClick={handleClearFilters}
                deleteIcon={<ClearIcon fontSize="small" />}
                onDelete={handleClearFilters}
                variant="outlined"
              />
            )}
          </Box>
        )}

      <Box sx={{ height: "calc(100% - 180px)" }}>
        {loadingNote ? (
          <Loading message="Cargando Notas..." />
        ) : filteredData.length === 0 ? (
          <DataNotFound />
        ) : (
          <NotesTable
            filteredData={filteredData}
            token={token}
            refreshNotes={() => setRefresh(!refresh)}
            searchTerm={searchTerm}
            setItems={setItems}
          />
        )}
      </Box>
    </Paper>
  );
};
